<template>
  <div class="plan-container plan-container__assign-audit">
    <p class="title">Live QHA Audits</p>
    <div class="sub-field">
      <p class="subtitle">Click on an Audit to view it's data.</p>
      <div class="search-params">
        <div class="dateFilter">
          Assigned from:
          <input type="date" name="search" v-model="dateRange.start" />
          To:
          <input type="date" name="search" v-model="dateRange.end" />
        </div>
        <div class="search-bar field no-margin">
          <span class="icon input-icon">
            <i class="fas fa-search"></i>
          </span>
          <input
            type="text"
            class="input has-icon"
            placeholder="Search by name or email"
            v-model="searchTerm"
            @input="search"
          />
        </div>
      </div>
    </div>
    <div class="list__options">
      <label for="page-size"> Results per page: </label>&nbsp;
      <input
        class="input"
        type="number"
        step="5"
        name="page-size"
        v-model.number="limit"
      />
    </div>
    <div class="plan-list">
      <div class="table-wrapper">
        <table class="table">
          <thead>
            <tr class="table-header">
              <th @click="sortBy('planId.internalId')">*C/YP ID</th>
              <th></th>
              <th @click="sortBy('userId.name')">Assigned to</th>
              <th @click="sortBy('deadlineDate')">Deadline</th>
              <th @click="sortBy('createdAt')">Assigned on</th>
              <th @click="sortBy('completedAt')">Completed on</th>
              <th v-if="canViewItem('admin')"></th>
            </tr>
          </thead>
          <tbody name="table-row" is="transition-group">
            <tr v-for="assignment in paginatedECHPS" :key="assignment._id">
              <td
                @click.stop="didClickShowAuditData(assignment._id)"
                class="link-hover"
              >
                {{ assignment.planId.internalId }}
              </td>
              <td>
                <i
                  v-if="isModeratedPlan(assignment)"
                  class="fas fa-user-friends"
                  style="color: #cecece"
                ></i>
              </td>
              <td>{{ assignment.userId.name }}</td>
              <td>{{ assignment.deadlineDate | formatDate }}</td>
              <td>{{ assignment.createdAt | formatDate }}</td>
              <td>{{ assignment.completedAt | formatDate }}</td>
              <td v-if="canViewItem('admin')">
                <div class="context-items">
                  <span class="completed" v-show="assignment.completedAt">
                    <i class="fas fa-check-circle" style="color: green"></i>
                  </span>
                  <span
                    class="pdf-report"
                    v-show="assignment.completedAt"
                    @click="
                      downloadPdf(
                        assignment._id,
                        `audit-summary-${assignment.planId.internalId}.pdf`
                      )
                    "
                  >
                    <i class="far fa-file-pdf"></i>
                  </span>
                  <span class="archive" @click="archive(assignment._id)">
                    <i class="far fa-trash-alt"></i>
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="list__controls">
        <vue-paginate-al
          :totalPage="totalPages"
          :currentPage="currentPage"
          @btnClick="goToFunction"
          customActiveBGColor="#00b3bb"
          :pageRange="4"
          nextText=">"
          prevText="<"
        >
        </vue-paginate-al>
      </div>
    </div>
    <div class="button-row"></div>
    <AuditDataPopup
      :audit="auditToView"
      @close-audit-data-popup="showAuditPopup = false"
      v-if="showAuditPopup"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import AuditDataPopup from '@/components/ViewAuditPopup/AuditDataPopup.vue'

import { format, isAfter, isBefore, parseISO } from 'date-fns'
import Fuse from 'fuse.js'

export default {
  name: 'LiveQHAs',
  components: { AuditDataPopup },
  data() {
    return {
      sortKey: 'updatedAt',
      searchTerm: '',
      fuse: null,
      searchResults: [],
      auditToView: null,
      showAuditPopup: false,
      orderToggle: false,
      limit: 10,
      offset: 0,
      lastPage: 0,
      dateRange: {
        start: null,
        end: null
      }
    }
  },
  watch: {
    assignments() {
      const plans = [...this.assignments].map(a => {
        return {
          ...a.planId,
          name: a.userId.name,
          email: a.userId.email,
          serviceArea: a.userId.service,
          assignmentId: a._id
        }
      })
      this.fuse = new Fuse(plans, {
        includeScore: true,
        ignoreLocation: true,
        threshold: 0.5,
        keys: ['internalId', 'name', 'email', 'serviceArea']
      })
    },
    showAuditPopup(val) {
      if (!val) {
        this.auditToView = null
      }
    },
    offset(newValue, oldValue) {
      this.offset = newValue
    },
    limit(newValue, oldValue) {
      this.limit = newValue
    }
  },
  computed: {
    ...mapGetters({
      assignments: 'assignment/assignments',
      audits: 'assignment/audits',
      role: 'user/role'
    }),
    plans() {
      return [...this.assignments].map(a => a.planId)
    },
    results() {
      if (!this.searchTerm) {
        return [...this.assignments].sort((a, b) =>
          a.updatedAt > b.updatedAt ? -1 : 1
        )
      }
      return [...this.assignments]
        .filter(a => this.searchResults.includes(a._id))
        .sort((a, b) => (a.updatedAt > b.updatedAt ? -1 : 1))
    },
    orderedAndFiltered() {
      if (!this.results) {
        return []
      }

      const keys = this.sortKey.split('.')
      let assignments = [...this.results]

      if (this.dateRange.start) {
        assignments = assignments.filter(
          ({ createdAt }) =>
            createdAt &&
            isAfter(parseISO(createdAt), parseISO(this.dateRange.start))
        )
      }

      if (this.dateRange.end) {
        assignments = assignments.filter(({ createdAt }) => {
          // Set time to EOD.
          const endDate = parseISO(this.dateRange.end)
          endDate.setHours(23)
          endDate.setMinutes(59)
          endDate.setSeconds(59)
          return createdAt && isBefore(parseISO(createdAt), endDate)
        })
      }

      assignments = assignments.sort((a, b) => {
        for (let i = 0; i < keys.length; i++) {
          a = a[keys[i]]
          b = b[keys[i]]
        }
        return this.orderToggle ? (a < b ? -1 : 1) : a > b ? -1 : 1
      })

      return assignments
    },
    totalPages() {
      return Math.ceil(this.orderedAndFiltered.length / this.limit)
    },
    currentPage() {
      if (this.offset === 0) {
        return 1
      }
      return this.offset / this.limit
    },
    paginatedECHPS() {
      const limit = this.offset + this.limit
      return this.orderedAndFiltered.slice(this.offset, limit)
    }
  },
  filters: {
    formatDate(val) {
      if (!val) {
        return ''
      }
      return format(new Date(val), 'PPP')
    }
  },
  methods: {
    ...mapActions({
      archiveAssignment: 'assignment/archive',
      fetchSummaryPDF: 'audit/fetchSummaryPDF'
    }),
    sortBy(toSort) {
      this.offset = 0
      this.orderToggle = this.sortKey === toSort ? !this.orderToggle : true
      this.sortKey = toSort
    },
    search(event) {
      if (!this.fuse) {
        const plans = [...this.assignments].map(a => {
          return {
            ...a.planId,
            name: a.userId.name,
            email: a.userId.email,
            serviceArea: a.userId.service,
            assignmentId: a._id
          }
        })
        this.fuse = new Fuse(plans, {
          includeScore: true,
          ignoreLocation: true,
          threshold: 0.5,
          keys: ['name', 'email']
        })
      }
      const results = this.fuse.search(this.searchTerm)

      this.searchResults = results.map(r => r.item.assignmentId)
    },
    didClickShowAuditData(assignmentId) {
      this.auditToView = this.audits.find(
        a =>
          // Must be this assignment ID and completed.
          a.assignmentId._id === assignmentId && !!a.completedAt
      )
      if (this.auditToView) {
        this.showAuditPopup = true
      }
    },
    isModeratedPlan(assignment) {
      return (
        this.orderedAndFiltered.find(
          a =>
            a._id !== assignment._id && a.planId._id === assignment.planId._id
        ) !== undefined
      )
    },
    async archive(assignmentId) {
      await this.archiveAssignment(assignmentId)
    },
    goToFunction(value) {
      if (value === this.lastPage) return
      this.offset =
        value > this.lastPage
          ? this.offset + this.limit
          : this.offset - this.limit
      this.lastPage = value
    },
    canViewItem(resource) {
      switch (resource) {
        case 'admin':
          return ['super', 'admin'].includes(this.role)
        default:
          return true
      }
    },
    downloadPdf(assignmentId, name) {
      this.fetchSummaryPDF({
        assignmentId,
        filename: name
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/style/variables.scss';
@import '@/style/components.scss';
@import '@/style/sections.scss';

.upload-container {
  width: 100%;
  position: relative;
  display: grid;
  grid-template-rows: 46px repeat(3, auto);
  grid-auto-flow: unset;
  &__title {
    margin: 0;
    font-weight: 600;
    font-size: 1.25rem;
    padding-left: 1rem;
  }
  &__subtitle {
    font-size: 1rem;
    font-weight: 400;
    padding-left: 1rem;
    &--bold {
      font-weight: 600;
    }
  }
  &__dropbox {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100px;
    border: 1px dashed lightgrey;
    border-radius: $border-radius;
    padding: 1rem;
    margin: 1rem;
    font-size: 1.25rem;
  }
  .is-active {
    background: #42ab983b;
  }
  .button-row {
    padding-top: $padding1x;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}

td.archive {
  width: 3rem;
  color: transparent;
  transition: all 200ms ease-in-out;
}

.table-row-enter-active,
.table-row-leave-active {
  transition: all 350ms;
}

.table-row-enter,
.table-row-leave-to {
  opacity: 0;
}

.table-row-move {
  transition: all 300ms;
}

td.archive > .fa-2x {
  font-size: 1.5rem;
}

.table .table-header > th {
  cursor: pointer;
}

.link-hover:hover {
  cursor: pointer;
  text-decoration: underline;
}

.sub-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  grid-area: search;
  .subtitle {
    width: 100%;
  }
}

.search-params {
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  height: 100%;
  width: 100%;
}

.dateFilter {
  flex-grow: 1;
  flex-shrink: 0;
  padding-right: 0.5rem;
}

.search-bar {
  max-width: 400px;
}

.control {
  display: flex;
  place-items: center;
  place-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid #eeedec;
  border-radius: 4px;
  background: transparent;
  box-shadow: none;
  transition: box-shadow 200ms;
  font-weight: 600;
  &:hover {
    box-shadow: 0 4px 8px #eeedec;
  }
}

.list__options {
  margin-top: 1rem;
  grid-area: limit;
  justify-self: flex-end;
  .input {
    max-width: 64px;
    padding-left: 1rem;
    font-weight: 600;
  }
}

.list__controls {
  grid-column: 1 / 3;
  justify-content: center;
  margin-bottom: 20px;
}

.list__controls,
.pagination-control {
  display: flex;
}
.pagination-control .control {
  border: none;
}

.link-hover:hover {
  cursor: pointer;
  text-decoration: underline;
}

.current-page {
  color: blue;
  text-decoration: underline;
}

::v-deep .pagination li a {
  color: black !important;
}

::v-deep .pagination li:hover {
  a {
    text-decoration: underline;
  }
}

::v-deep .pagination .active a {
  color: white !important;
}

.context-items {
  text-align: right;
}
.context-items span {
  font-size: 1.2rem;
  margin-left: 15px;
  width: 20px;
  text-align: center;
  display: inline-block;
}
.context-items span.archive:hover,
.context-items span.pdf-report:hover {
  transform: scale(1.3);
}
</style>
